/**
 * Plays the damage-taken sound when a raid encounter actually costs the player
 * HP, and picks between the melee and the bullet recording by what attacked.
 *
 * ─── Why raid steps and not the health sheet ────────────────────────────────
 * The obvious signal is `health:updated` — it arrives on every HP change and
 * `gameStateStore` already holds it. It is the wrong one, because it carries
 * no cause. Two recordings were supplied, "damage by a melee weapon" and
 * "damage from bullets", and an HP number cannot choose between them. Choosing
 * on the client would mean inventing a rule the server does not share, which
 * is the same objection that keeps the two PvP scores unwired (`tracks.ts`).
 *
 * Raid steps do carry the cause. Each resolved step's `encounter` has a
 * `threatType` the server picked from the encounter row, plus the `hpLoss`
 * that survived the armour-as-shield model. `bandit` shoots, `predator`
 * bites — that mapping is the server's classification, read, not derived.
 *
 * ─── Why the first array a raid shows is always silent ──────────────────────
 * `pollRaidStatus` does not stream. It replays the whole timeline from the
 * seed and returns EVERY step elapsed so far, so a player who reloads the page
 * forty minutes into a raid receives a dozen encounters in one array. A naive
 * diff would fire a dozen damage sounds into each other on page load.
 *
 * So the first array seen for a given raid sets the baseline and plays
 * nothing, exactly as `level-up-sound.ts` treats the first character sheet.
 * The baseline is keyed to the RAID ID, not to a step number: `resetRaid()`
 * clears `steps` to `[]` between runs, and a session-global high-water mark
 * would swallow the opening steps of the next raid.
 *
 * ─── One sound per poll, not one per step ───────────────────────────────────
 * `stepDurationMs` can be shorter than the poll interval, so a single response
 * can advance several steps. Two copies of the same transient a few
 * milliseconds apart do not read as two hits; they read as distortion. A batch
 * therefore plays at most one sound, chosen from the LAST damaging encounter
 * in it — the most recent thing that happened is the one worth hearing.
 *
 * ─── And why there is no retrigger guard, unlike `weapon-sound.ts` ──────────
 * That one needs a 60 ms floor because it fires from an unrate-limited pointer
 * handler. This one cannot stack: `pollIntervalMs` is 2000 ms and both impact
 * clips are under 0.7 s, so two plays are at minimum three clip-lengths apart
 * even when consecutive polls each carry a hit. The batch rule covers within a
 * poll; the poll interval covers between them. If that interval is ever
 * shortened below ~1 s, this needs the same guard `weapon-sound.ts` has.
 *
 * ─── Unrecognised threats are silent on purpose ─────────────────────────────
 * `threatType` is a bare string on the encounter row, not an enum, and rows
 * are editable in the admin panel. `hazard` — radiation — is neither a bullet
 * nor a blade and gets nothing. Anything unmapped gets nothing and one dev
 * log, the same contract `weapon-sound.ts` gives an unsounded weapon: this
 * path swallows its own errors, so "no sound for this yet" and "audio is
 * broken" are indistinguishable without it.
 */
import { raidStateStore } from '../state/raid-state';
import type { RaidStepResult } from '../state/raid-state.types';
import type { MixerHandle } from './mixer';
import { TRACKS } from './tracks';

/**
 * Server `threatType` -> recording.
 *
 * The seeded encounters are `bandit` (bandit_scout, raider_squad), `predator`
 * (shambler, feral_pack, deep_stalker) and `hazard` (rad_hazard). Only the
 * first two are something that hits you.
 */
export const THREAT_SOUND: Record<string, keyof typeof TRACKS> = {
  bandit: 'hitBullet',
  predator: 'hitMelee',
  // `hazard` is deliberately absent — rad exposure is not an impact, and
  // playing a gunshot wound for it would be a lie about what happened.
};

/** The clip a threat should play, or `undefined` if it has none. */
export function trackForThreat(threatType: string | null | undefined): keyof typeof TRACKS | undefined {
  return threatType ? THREAT_SOUND[threatType] : undefined;
}

/**
 * The last damaging encounter in `steps` beyond index `from`, or `undefined`.
 *
 * Split out from the subscription so the batching rule is testable without a
 * store, a mixer or a clock.
 */
export function lastDamagingEncounter(
  steps: readonly RaidStepResult[],
  from: number,
): RaidStepResult['encounter'] | undefined {
  for (let i = steps.length - 1; i >= from; i -= 1) {
    const enc = steps[i]?.encounter;
    // `hpLoss` is post-mitigation, so a fight won without a scratch, and a
    // fight whose damage the armour shield fully absorbed, both land at 0 and
    // are correctly silent. The player felt nothing.
    if (enc && enc.hpLoss > 0) return enc;
  }
  return undefined;
}

/** Starts watching. Returns an unsubscribe fn. */
export function createDamageSound(mixer: MixerHandle): () => void {
  for (const id of new Set(Object.values(THREAT_SOUND))) {
    void mixer.load(TRACKS[id].path).catch(() => { /* retried by the play path */ });
  }

  // `null` means "no raid seen yet", which is distinct from any real raid id.
  let baselineRaidId: string | null = null;
  // The highest STEP NUMBER seen, not an array length. The server now sends
  // only the most recent steps of a long raid (a window, not the whole log), so
  // the array stops growing once it is full while step numbers keep climbing.
  let seenStep = -1;
  const warned = new Set<string>();

  return raidStateStore.subscribe((s) => {
    const { raidId, steps } = s;
    if (!raidId) {
      // Between raids. Drop the baseline so the next one re-establishes its
      // own rather than inheriting a step count from the last.
      baselineRaidId = null;
      seenStep = -1;
      return;
    }

    if (raidId !== baselineRaidId) {
      baselineRaidId = raidId;
      seenStep = steps.length > 0 ? steps[steps.length - 1]!.step : -1;
      return; // first sight of this raid — baseline, never a sound
    }

    // A shrinking array should not make `seenSteps` go backwards and replay:
    // the timeline is append-only, but a partial response is cheaper to guard
    // against than to reason about.
    const newest = steps.length > 0 ? steps[steps.length - 1]!.step : -1;
    if (newest <= seenStep) return;

    const previous = seenStep;
    seenStep = newest;
    const from = steps.findIndex((st) => st.step > previous);

    const enc = lastDamagingEncounter(steps, from);
    if (!enc) return;

    const id = trackForThreat(enc.threatType);
    if (!id) {
      if (import.meta.env?.DEV && !warned.has(enc.threatType)) {
        warned.add(enc.threatType);
        console.info(
          `[damage-sound] no recording for threatType '${enc.threatType}' — `
          + 'taking damage from it is silent on purpose, not a load failure. '
          + `Sounded threats: ${Object.keys(THREAT_SOUND).join(', ')}.`,
        );
      }
      return;
    }

    // Audio not unlocked yet. Dropping it beats queueing, which would fire the
    // hit at whatever gesture later resumes the context.
    if (!mixer.isRunning()) return;

    const track = TRACKS[id];
    void mixer.playOnce(track.path, track.channel).catch(() => {
      // A missing clip must not throw out of a store subscription — every
      // other subscriber of this store would stop being notified.
    });
  });
}
